import { DropTarget } from "../GalleryItemsLayout";

export function getThumbnailSize(displaySize: number): 24 | 292 | 480 | 1232 {
  // Size 1-2 are largest (50%+ width) - use 1232
  if (displaySize <= 2) return 1232;
  // Size 3-4 are medium (25-33% width) - use 480
  if (displaySize <= 4) return 480;
  // Size 5-6 are smallest (16-20% width) - use 292
  return 292;
}

export function findDropTarget(e: DragEvent, cachedItems?: HTMLElement[]): DropTarget | null {
  const point = { x: e.clientX, y: e.clientY };

  const droppableItems =
    cachedItems ??
    (Array.from(document.querySelectorAll("[data-droppable-item]")) as HTMLElement[]);

  if (droppableItems.length === 0) return null;

  let closestItem: HTMLElement | null = null;
  let closestSameRowItem: HTMLElement | null = null;
  let minDistance = Infinity;
  let minSameRowDistance = Infinity;

  for (const item of droppableItems) {
    const rect = item.getBoundingClientRect();

    const closestX = Math.max(rect.left, Math.min(point.x, rect.right));
    const closestY = Math.max(rect.top, Math.min(point.y, rect.bottom));

    const distance = Math.sqrt(Math.pow(point.x - closestX, 2) + Math.pow(point.y - closestY, 2));

    // Track overall closest
    if (distance < minDistance) {
      minDistance = distance;
      closestItem = item;
    }

    // Track closest on same row (cursor Y is within item's vertical bounds)
    const isOnSameRow = point.y >= rect.top && point.y <= rect.bottom;
    if (isOnSameRow) {
      const horizontalDistance = Math.abs(point.x - closestX);
      if (horizontalDistance < minSameRowDistance) {
        minSameRowDistance = horizontalDistance;
        closestSameRowItem = item;
      }
    }
  }

  // Prefer same row item if found
  const targetItem = closestSameRowItem ?? closestItem;

  if (!targetItem) return null;

  const itemRect = targetItem.getBoundingClientRect();
  const itemIndex = parseInt(targetItem.getAttribute("data-item-index") || "0");
  const isFullWidth = targetItem.getAttribute("data-full-width") === "true";

  const itemCenterX = itemRect.left + itemRect.width / 2;
  const itemCenterY = itemRect.top + itemRect.height / 2;

  let position: "before" | "after";

  if (Math.abs(point.y - itemCenterY) < itemRect.height / 4) {
    position = point.x < itemCenterX ? "before" : "after";
  } else if (point.y < itemCenterY) {
    position = "before";
  } else {
    position = "after";
  }

  if (position === "before" && itemIndex > 0) {
    const distanceToTop = Math.abs(point.y - itemRect.top);
    if (distanceToTop < 30) {
      const prevItem = droppableItems.find(
        el => el.getAttribute("data-item-index") === String(itemIndex - 1)
      );
      if (prevItem) {
        const prevIsFullWidth = prevItem.getAttribute("data-full-width") === "true";
        if (prevIsFullWidth) {
          return {
            type: "item",
            element: prevItem,
            index: itemIndex - 1,
            position: "after",
            cursorX: point.x,
            cursorY: point.y,
          };
        }
      }
    }
  }

  if (position === "after") {
    const distanceToBottom = Math.abs(point.y - itemRect.bottom);

    if (distanceToBottom < 30) {
      const nextItem = droppableItems.find(
        el => el.getAttribute("data-item-index") === String(itemIndex + 1)
      );
      if (nextItem) {
        const nextItemRect = nextItem.getBoundingClientRect();
        const cursorIsOverNextItem = point.x >= nextItemRect.left && point.x <= nextItemRect.right;

        if (cursorIsOverNextItem || isFullWidth) {
          return {
            type: "item",
            element: nextItem,
            index: itemIndex + 1,
            position: "before",
            cursorX: point.x,
            cursorY: point.y,
          };
        }
      }
    }
  }

  return {
    type: "item",
    element: targetItem,
    index: itemIndex,
    position,
    cursorX: point.x,
    cursorY: point.y,
  };
}
